import React, { useState, useEffect } from 'react';
import { PenTool as Token, Check, CreditCard, Coins, ShoppingBag, ArrowRight, Loader2 } from 'lucide-react';
import { STRIPE_PRODUCTS, getPriceForCurrency } from '../stripe-config';
import { TokenTransactionType, Currency } from '../types';
import { createCheckoutSession } from '../services/stripeService';
import { analyticsService } from '../services/analyticsService';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import CurrencySelector from './CurrencySelector';

interface TokenPurchaseProps {
  userId: string;
  onPurchaseComplete: (success: boolean) => void;
}

const TokenPurchase: React.FC<TokenPurchaseProps> = ({ userId, onPurchaseComplete }) => {
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { t, language } = useLanguage();
  const { currency, convertPrice, formatPrice } = useCurrency();
  
  // Track component load in analytics
  useEffect(() => {
    analyticsService.trackCustomEvent('token_purchase_view');
  }, []);
  
  const handleSelectPackage = (packageId: string) => {
    setSelectedPackage(packageId);
    setError(null);
    
    // Track package selection in analytics
    analyticsService.trackCustomEvent('token_package_selected', {
      package_id: packageId
    });
  };
  
  const handleProceedToCheckout = async () => {
    if (!selectedPackage) {
      setError(t('errors.missingInfo'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Proceeding to checkout for package:', selectedPackage, 'with currency:', currency);

      // Create checkout session with selected currency
      const result = await createCheckoutSession(selectedPackage, currency);
      console.log('Checkout session result:', result);
      
      if (!result || !result.url) {
        throw new Error('No se recibió la URL de checkout del servidor');
      }
      
      // Track proceeding to checkout
      analyticsService.trackCustomEvent('proceed_to_checkout', {
        package_id: selectedPackage
      });
      
      // Redirect to Stripe Checkout
      window.location.href = result.url;
    } catch (err: any) {
      console.error('Error in handleProceedToCheckout:', err);
      setError(err.message || t('errors.unknownError'));
      
      // Track checkout error
      analyticsService.trackCustomEvent('checkout_setup_error', {
        error_message: err.message || 'Unknown error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <h3 className="font-semibold text-gray-800 flex items-center">
          <ShoppingBag className="h-4 w-4 mr-2" />
          {t('tokens.purchase.title')}
        </h3>
      </div>
      
      <div className="p-4">
        <div className="mb-4">
          <CurrencySelector compact />
        </div>

        <p className="text-sm text-gray-600 mb-4">
          {t('tokens.purchase.subtitle')}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {STRIPE_PRODUCTS.map((pkg) => {
            // Use the translationKey to get the correct translation
            const packageName = t(`tokens.packages.${pkg.translationKey}.name`);
            const packageDescription = t(`tokens.packages.${pkg.translationKey}.description`);
            
            return (
              <div 
                key={pkg.id}
                onClick={() => handleSelectPackage(pkg.id)}
                className={`
                  border rounded-lg p-3 cursor-pointer transition-all relative
                  ${selectedPackage === pkg.id 
                    ? 'border-primary bg-blue-50 shadow-md' 
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }
                  ${pkg.isPopular ? 'ring-2 ring-primary ring-opacity-50' : ''}
                `}
              >
                {pkg.isPopular && (
                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                    {t('tokens.purchase.mostPopular')}
                  </div>
                )}
                
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-gray-800">{packageName}</h4>
                    <p className="text-xs text-gray-500 mt-1">{packageDescription}</p>
                  </div>
                  
                  <div className="flex items-center bg-gray-100 px-2 py-1 rounded">
                    <Coins className="h-3 w-3 text-primary mr-1" />
                    <span className="text-xs font-medium">{pkg.tokens} {t('tokens.purchase.tokens')}</span>
                  </div>
                </div>
                
                <div className="mt-3 flex justify-between items-end">
                  <span className="text-lg font-bold text-primary">
                   {(() => {
                     const priceInCents = getPriceForCurrency(pkg.id, currency);
                     if (!priceInCents || priceInCents === 0) {
                       return (
                         <span className="text-green-600 font-bold">
                           {language === 'es' ? 'GRATIS' : 'FREE'}
                         </span>
                       );
                     }
                     return formatPrice(priceInCents / 100, currency);
                   })()}
                  </span>
                  
                  <div 
                    className={`
                      w-5 h-5 rounded-full border-2 flex items-center justify-center
                      ${selectedPackage === pkg.id 
                        ? 'border-primary bg-primary' 
                        : 'border-gray-300'
                      }
                    `}
                  >
                    {selectedPackage === pkg.id && (
                      <Check className="h-3 w-3 text-white" />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
            {error}
          </div>
        )}
        
        <button 
          onClick={handleProceedToCheckout}
          disabled={loading || !selectedPackage}
          className={`w-full py-2 px-4 rounded-lg transition-colors flex items-center justify-center
            ${loading || !selectedPackage 
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
              : 'bg-primary text-white hover:bg-primary-dark'}
          `}
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin h-5 w-5 mr-2" />
              Preparando checkout...
            </>
          ) : (
            <>
              Continuar al Checkout
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default TokenPurchase;